import React, { BaseSyntheticEvent, ReactNode, useEffect, useState } from 'react';
import { Controller, ControllerRenderProps } from 'react-hook-form';
import FormLabel from 'components/atoms/FormLabel';
import FormErrorLabel from 'components/atoms/FormErrorLabel';
import { StyledFormElementWrapper } from 'theme/styles';
import { IFormElementProps } from 'models/form';
import { Box, IconButton, SxProps, Typography } from '@mui/material';
import { EIconTypes } from 'constants/Icons';
import Icon from 'components/atoms/Icon/Icon';
import { useTranslations } from 'hooks/useTranslations';
import { get } from 'react-hook-form';
import { colors } from 'theme/colors';
import { StyledTextField } from './styles';
import { getNestedErrorMessage } from './helpers';

export enum EFormInputTypes {
  text = 'text',
  textarea = 'textarea',
  password = 'password',
  number = 'number',
}

interface IFormInput extends IFormElementProps {
  placeholder?: string;
  multiline?: boolean;
  minRows?: number;
  rows?: number;
  type?: EFormInputTypes;
  sx?: SxProps;
  className?: string;
  InputProps?: object;
  disabled?: boolean;
  required?: boolean;
  arrayName?: string;
  arrayIndex?: number;
  setValue?: any;
  fieldName?: string;
  helperText?: string;
  charsLimit?: number;
  maxLength?: boolean;
  maxLengthNumber?: boolean;
  defaultValue?: any;
  withValidation?: boolean;
  withLabel?: boolean;
  showCounter?: boolean;
  disableCopyPaste?: boolean;
  onClear?: () => void;
  endAdornmentText?: string;
  noWrap?: boolean;
  tooltip?: ReactNode;
  fullWidth?: boolean;
  blurred?: boolean;
  trim?: boolean;
}

export const FormInput = ({
  name,
  type = EFormInputTypes.text,
  label,
  placeholder,
  multiline,
  minRows,
  rows,
  control,
  errors,
  sx,
  setValue,
  className,
  InputProps,
  disabled,
  required,
  arrayName,
  arrayIndex,
  fieldName,
  helperText,
  charsLimit,
  maxLength = false,
  maxLengthNumber = false,
  defaultValue,
  withValidation = true,
  withLabel = true,
  showCounter = false,
  disableCopyPaste = false,
  onClear,
  endAdornmentText,
  noWrap,
  tooltip,
  fullWidth,
  blurred = false,
  trim = true,
}: IFormInput) => {
  const { t } = useTranslations();
  const [error, setError] = useState<string>('');
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  showCounter = showCounter || type === EFormInputTypes.textarea;

  useEffect(() => {
    let isError = false;
    const errorMessage = get(errors, name)?.message;
    if (errorMessage) {
      isError = true;
      setError(errorMessage || '');
    } else if (name.includes('.') && !name.includes('[') && Object.keys(errors).length) {
      const keys = name.split('.');
      const errorMessage = getNestedErrorMessage(errors, keys);
      if (keys && errorMessage) {
        isError = true;
        setError(errorMessage);
      }
    } else {
      const errorMessage =
        arrayName && arrayIndex !== undefined && fieldName
          ? errors?.[arrayName]?.[arrayIndex]?.[fieldName]?.message
          : errors[name]?.message;

      isError = true;
      setError(errorMessage);
    }
    if (!isError) {
      setError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors, name]);

  const renderEndAdornment = (field: ControllerRenderProps) => {
    if (type === EFormInputTypes.password) {
      return (
        <IconButton
          sx={{
            maxHeight: '32px',
            maxWidth: '32px',
          }}
          onClick={() => setIsPasswordVisible((data) => !data)}
        >
          <Icon
            type={isPasswordVisible ? EIconTypes.view : EIconTypes.hidden}
            color={disabled ? colors.gray400 : colors.gray900}
          />
        </IconButton>
      );
    }

    const showClearButton =
      field.value !== '' &&
      !disabled &&
      setValue &&
      field.value !== undefined &&
      field.value !== null;

    if (showClearButton || endAdornmentText) {
      return (
        <Box
          sx={{
            backgroundColor: disabled ? colors.gray100 : 'inherit',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'row',
          }}
        >
          {showClearButton && (
            <IconButton
              sx={{
                maxHeight: '32px',
                maxWidth: '32px',
              }}
              onClick={() => {
                setValue(name, '');
                setError('');
                if (onClear) onClear();
              }}
            >
              <Icon
                type={EIconTypes.clear}
                color={disabled ? colors.gray400 : colors.gray900}
              />
            </IconButton>
          )}
          {endAdornmentText && !disabled && (
            <Typography variant="body2" sx={{ paddingLeft: 1, paddingRight: 2 }}>
              {endAdornmentText}
            </Typography>
          )}
        </Box>
      );
    }
    return null;
  };

  return (
    <StyledFormElementWrapper fullWidth={fullWidth}>
      {withLabel && (
        <FormLabel
          name={name}
          label={label}
          disabled={disabled}
          required={required}
          noWrap={noWrap}
          tooltip={tooltip}
        />
      )}
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          const currentLength = field.value ? String(field.value).length : 0;
          const computedCounterText = `${currentLength}/${charsLimit}`;

          return (
            <StyledTextField
              {...field}
              onFocus={() => setIsFocused(true)}
              onBlur={(e) => {
                setIsFocused(false);
                const canTrim =
                  trim && (type === EFormInputTypes.text || type === EFormInputTypes.textarea);
                if (
                  canTrim &&
                  typeof field.value === 'string' &&
                  field.value !== field.value.trim()
                ) {
                  field.onChange(field.value.trim());
                }
                field.onBlur();
              }}
              sx={{
                ...sx,
                ...(blurred && !isFocused && field.value
                  ? {
                      '& input': { filter: 'blur(4px)', userSelect: 'none' },
                      '& textarea': { filter: 'blur(4px)', userSelect: 'none' },
                    }
                  : {}),
              }}
              defaultValue={defaultValue}
              className={className}
              disabled={disabled}
              slotProps={{
                formHelperText: {
                  sx: {
                    textAlign: 'right',
                    marginRight: 0,
                    marginLeft: 0,
                  },
                },
                input: {
                  inputProps: {
                    ...((showCounter || maxLength) && charsLimit
                      ? { maxLength: charsLimit }
                      : {}),
                  },
                  ...InputProps,
                  endAdornment: renderEndAdornment(field),
                },
              }}
              type={isPasswordVisible ? 'text' : type}
              id={name}
              variant="outlined"
              placeholder={placeholder}
              multiline={multiline}
              minRows={minRows}
              rows={rows}
              error={!!error}
              onPaste={(e: any) => {
                if (disableCopyPaste) {
                  e.preventDefault();
                  return false;
                }
                return true;
              }}
              onCopy={(e: any) => {
                if (disableCopyPaste) {
                  e.preventDefault();
                  return false;
                }
                return true;
              }}
              helperText={showCounter ? computedCounterText : helperText}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (type === EFormInputTypes.number) {
                  if (e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
                    e.preventDefault();
                  }
                }
              }}
              onChange={(e: BaseSyntheticEvent) => {
                setError('');
                let value = e.target.value;
                if (maxLengthNumber && charsLimit) {
                  value = value.slice(0, charsLimit);
                }
                field.onChange(value || '');
                if (charsLimit && !showCounter && charsLimit < e.target.value.length) {
                  setError(
                    t('general.field.validation.maxCharacters').replace(
                      '{0}',
                      charsLimit.toString(),
                    ),
                  );
                }
              }}
            />
          );
        }}
      />
      {withValidation && (
        <FormErrorLabel label={error} style={{ marginTop: showCounter ? '-17px' : 0 }} />
      )}
    </StyledFormElementWrapper>
  );
};
